import {
    Mail,
    FileText,
    Mic,
    Workflow,
    MessagesSquare,
    Plug,
    Sparkles,
    Ship,
    Anchor,
    Boxes,
    Truck,
    FileCheck2,
    Container,
    Search,
    Eye,
    Target,
    FlaskConical,
    Rocket,
    TrendingUp,
    Handshake,
    Users,
    Clock,
} from "lucide-react";

export const NAV_LINKS = [
    { label: "Solutions", to: "/solutions" },
    { label: "Our Approach", to: "/approach" },
    { label: "Insights", to: "/insights" },
    { label: "About", to: "/about" },
    { label: "Contact", to: "/contact" },
];

export const HIDDEN_COSTS = [
    {
        title: "Too Many Emails",
        body: "Operations teams spend hours processing shipment requests and customer communications.",
        metric: "40%",
        metricLabel: "of the day in the inbox",
    },
    {
        title: "Manual Data Entry",
        body: "Information is copied repeatedly between emails, spreadsheets and business systems.",
        metric: "3x",
        metricLabel: "duplicated keystrokes",
    },
    {
        title: "Document Processing",
        body: "Bills of Lading, invoices and shipping documents consume valuable time.",
        metric: "12 min",
        metricLabel: "per document, on average",
    },
    {
        title: "Customer Communication",
        body: "Teams repeatedly answer the same operational questions.",
        metric: "60%",
        metricLabel: "of queries are repetitive",
    },
    {
        title: "Repetitive Workflows",
        body: "People spend time on administration instead of solving problems.",
        metric: "1000s",
        metricLabel: "of hours every year",
    },
];

export const CAPABILITIES = [
    {
        slug: "email-automation",
        title: "Email Automation",
        icon: Mail,
        desc: "Shipment requests and routine correspondence, read, understood and actioned automatically.",
    },
    {
        slug: "document-automation",
        title: "Document Automation",
        icon: FileText,
        desc: "Bills of Lading, invoices and customs paperwork extracted and routed in seconds.",
    },
    {
        slug: "voice-ai",
        title: "Voice AI",
        icon: Mic,
        desc: "Inbound operational calls handled, logged and escalated to the right person.",
    },
    {
        slug: "workflow-automation",
        title: "Workflow Automation",
        icon: Workflow,
        desc: "Multi-step operational processes connected end to end across your systems.",
    },
    {
        slug: "customer-communication",
        title: "Customer Communication",
        icon: MessagesSquare,
        desc: "Status updates and common questions answered instantly, in your tone of voice.",
    },
    {
        slug: "ai-integrations",
        title: "AI Integrations",
        icon: Plug,
        desc: "Automation embedded directly into the tools your operation already runs on.",
    },
    {
        slug: "custom-ai-solutions",
        title: "Custom AI Solutions",
        icon: Sparkles,
        desc: "Bespoke automation designed around the workflows unique to your business.",
    },
];

export const APPROACH_STEPS = [
    {
        n: "01",
        title: "Understand",
        body: "We learn how your operation works today.",
        icon: Search,
    },
    {
        n: "02",
        title: "Observe",
        body: "We analyse workflows and repetitive work.",
        icon: Eye,
    },
    {
        n: "03",
        title: "Identify",
        body: "We find high-value automation opportunities.",
        icon: Target,
    },
    {
        n: "04",
        title: "Prototype",
        body: "We automate one workflow first.",
        icon: FlaskConical,
    },
    {
        n: "05",
        title: "Deploy",
        body: "Integrate into existing systems.",
        icon: Rocket,
    },
    {
        n: "06",
        title: "Improve",
        body: "Continuously optimise based on real usage.",
        icon: TrendingUp,
    },
];

export const WHY_US = [
    {
        title: "Practical",
        body: "We focus on business outcomes, not technology for its own sake.",
        icon: Target,
    },
    {
        title: "Collaborative",
        body: "Every automation is built around your existing operations.",
        icon: Handshake,
    },
    {
        title: "Long-Term Partnership",
        body: "We become your automation partner as your business grows.",
        icon: Users,
    },
];

export const INDUSTRIES = [
    { title: "Freight Forwarders", icon: Boxes },
    { title: "Shipping Companies", icon: Ship },
    { title: "Ship Management", icon: Anchor },
    { title: "Logistics Providers", icon: Truck },
    { title: "Customs Brokers", icon: FileCheck2 },
    { title: "Ports & Terminals", icon: Container },
];

export const ASSESSMENT_CHECKLIST = [
    "Understand your operation",
    "Identify repetitive work",
    "Recommend automation opportunities",
    "Estimate ROI",
];

export const IMAGINE_POINTS = [
    "Customer emails were processed automatically.",
    "Shipment documents extracted in seconds.",
    "Routine customer questions answered instantly.",
    "Repetitive data entry disappeared.",
    "Your team focused on solving problems, not administration.",
];

export const INSIGHTS = [
    {
        title: "Reducing Email Overload in Shipping Operations",
        category: "Operations",
        date: "May 2026",
        readTime: "6 min read",
        excerpt:
            "The inbox has quietly become the most expensive system in logistics. Here is how to reclaim the hours it consumes.",
    },
    {
        title: "How AI Changes Maritime Operations",
        category: "Perspective",
        date: "Apr 2026",
        readTime: "8 min read",
        excerpt:
            "Automation is not about replacing seafarers or planners. It is about removing the repetitive work that surrounds them.",
    },
    {
        title: "The Future of Logistics Automation",
        category: "Strategy",
        date: "Mar 2026",
        readTime: "7 min read",
        excerpt:
            "Where the next decade of operational efficiency will come from, and why it starts with one workflow.",
    },
    {
        title: "Why Operational Teams Burn Out",
        category: "People",
        date: "Feb 2026",
        readTime: "5 min read",
        excerpt:
            "Burnout in operations is rarely about volume. It is about the repetitive, low-value work that never ends.",
    },
];

export const CHALLENGE_OPTIONS = [
    "Email & communication overload",
    "Document & paperwork processing",
    "Manual data entry between systems",
    "Repetitive customer questions",
    "Workflow visibility & handoffs",
    "Something else",
];

export const STATS = [
    { value: 1200, suffix: "+", label: "Hours reclaimed per team, yearly" },
    { value: 6, suffix: "-step", label: "Structured automation approach" },
    { value: 1, suffix: "", label: "Workflow automated first, then scaled" },
    { value: 100, suffix: "%", label: "Built around your existing systems" },
];

export const IMAGES = {
    heroPort:
        "https://images.unsplash.com/photo-1562892302-97faedd66f1c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    aerialContainers:
        "https://images.pexels.com/photos/2231744/pexels-photo-2231744.jpeg?auto=compress&cs=tinysrgb&w=1600",
    portDusk:
        "https://images.pexels.com/photos/21234960/pexels-photo-21234960.jpeg?auto=compress&cs=tinysrgb&w=1600",
    warehouse:
        "https://images.unsplash.com/photo-1740914994657-f1cdffdc418e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    craneDusk:
        "https://images.unsplash.com/photo-1770372762108-acee3a5acc52?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    shipDocked:
        "https://images.unsplash.com/photo-1561702469-c4239ced3f47?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
};

export { Clock };
